import { useEffect, useState } from 'react';
import { TypedEventTarget } from 'typescript-event-target';

import { useAppServices } from 'container/depContext';

import { useAuthCache } from './cache';
import { User, UserProfile } from './endpoints/users';
import { ReadingCacheType, useReadAPIWithServices } from './useAPIWithServices';

const TENANT_ID_SESSION_STORAGE_ITEM = 'current_tenant_id';

interface TenantCacheEvents {
  tenantIdUpdate: CustomEvent<string>;
}

class TenantCache extends TypedEventTarget<TenantCacheEvents> {
  constructor() {
    super();
  }

  get tenantId(): string | undefined {
    const value =
      window.sessionStorage.getItem(TENANT_ID_SESSION_STORAGE_ITEM) ??
      undefined;

    return value;
  }

  private set tenantId(newValue: string) {
    window.sessionStorage.setItem(TENANT_ID_SESSION_STORAGE_ITEM, newValue);
  }

  handleUpdate(userProfile: UserProfile) {
    this.tenantId = userProfile.currentTenant.id;
    this.dispatchTypedEvent(
      'tenantIdUpdate',
      new CustomEvent('tenantIdUpdate', {
        detail: userProfile.currentTenant.id,
      })
    );
  }

  initFromUser(user: User) {
    // don't overwrite what we already have if we already have a tenant
    if (this.tenantId) return;

    this.handleUpdate(user.profile);
  }

  clear() {
    window.sessionStorage.removeItem(TENANT_ID_SESSION_STORAGE_ITEM);
  }
}

export const tenantCache = new TenantCache();

export const useTenantCache = (_tenantCache: TenantCache = tenantCache) => {
  const [currentTenantId, setCurrentTenantId] = useState(_tenantCache.tenantId);
  useEffect(() => {
    const listener = (newTenantId: CustomEvent<string>): void => {
      setCurrentTenantId(newTenantId.detail);
    };

    _tenantCache.addEventListener('tenantIdUpdate', listener);

    return () => {
      _tenantCache.removeEventListener('tenantIdUpdate', listener);
    };
  }, [_tenantCache]);

  return {
    currentTenantId,
  };
};

export const useCurrentTenant = () => {
  const { loggedIn } = useAuthCache();

  const { currentTenantId } = useTenantCache();

  const { getTenantsRepo } = useAppServices();
  const { getTenants } = getTenantsRepo();
  const { data: tenants } = useReadAPIWithServices(
    [getTenants.name, loggedIn || undefined],
    () => getTenants(),
    {
      cacheType: ReadingCacheType.Fresh,
    }
  );

  const currentTenant = tenants?.data?.find(
    (tenant) => tenant.id === currentTenantId
  );

  return currentTenant;
};
